<template>
    <HeaderComponent />
    <ScrollableView class="open-bills-view-container view-container" contentClass="open-bills-view">
        <TabView class="open-bills-tab-view" @tabChange="onTabChange">
            <TabPanel contentClass="open-bills-tab">
                <template v-slot:header>
                    {{ translations.openBills.ordersForNow }} ({{ orderForNowBillsCount }})
                </template>

                <OpenBillsList
                    v-if="!isLoading"
                    ref="openOrderForNowBillsListComponent"
                    :isMobileView="false"
                    :disableCurrentTable="false"
                    :billListType="BillListType.OrdersForNowOnly"
                    @tableSelected="allocateToTable"
                    @roomSelected="openRoomPlan"
                    @openBillsLoadingError="onBillsLoadingError"
                />
                <div class="loading-placeholder" v-if="isLoading">{{ translations.openBills.loadingData }}</div>
            </TabPanel>
            <TabPanel contentClass="open-bills-tab">
                <template v-slot:header>
                    {{ translations.openBills.ordersForNowOrLater }} ({{ orderForNowOrLaterBillsCount }})
                </template>

                <OpenBillsList
                    v-if="!isLoading"
                    ref="openOrderForNowOrLaterBillsListComponent"
                    :isMobileView="false"
                    :disableCurrentTable="false"
                    :billListType="BillListType.OrdersForNowOrLaterOnly"
                    @tableSelected="allocateToTable"
                    @roomSelected="openRoomPlan"
                    @openBillsLoadingError="onBillsLoadingError"
                />
                <div class="loading-placeholder" v-if="isLoading">{{ translations.openBills.loadingData }}</div>
            </TabPanel>
        </TabView>
    </ScrollableView>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { OpenBillRoomSelectedEventData, OpenBillSelectedEventData } from "@/data/model/Bill";
    import { BillListType } from "@/data/model/Enums";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import OpenBillsList from "@/components/bill/OpenBillsList.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import HeaderComponent from "@/operator/components/layout/HeaderComponent.vue";
    import TabPanel from "primevue/tabpanel";
    import TabView from "primevue/tabview";

    // state
    const isLoading = ref(false);
    const isRefreshingOpenBills = ref(false);
    const isOpenBillsRefreshRequested = ref(false);

    const orderForNowBillsCount = computed(() => openOrderForNowBillsListComponent.value?.openBillsCount ?? 0);
    const orderForNowOrLaterBillsCount = computed(
        () => openOrderForNowOrLaterBillsListComponent.value?.openBillsCount ?? 0
    );

    // define html refs
    const openOrderForNowBillsListComponent = ref<InstanceType<typeof OpenBillsList> | null>(null);
    const openOrderForNowOrLaterBillsListComponent = ref<InstanceType<typeof OpenBillsList> | null>(null);

    // functions
    async function allocateToTable(eventData: OpenBillSelectedEventData) {
        isLoading.value = true;
        try {
            operatorEvents.showOrderAndPayView.dispatch({
                billId: eventData.selectedOpenBill.id,
                tableId: eventData.tableId,
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                message: translations.allocation.allocationFailed,
            });
        } finally {
            isLoading.value = false;
        }
    }

    async function openRoomPlan(eventData: OpenBillRoomSelectedEventData) {
        isLoading.value = true;
        try {
            operatorEvents.views.ShowRoomPlan.dispatch({
                showSettingsRoomPlan: false,
                locationId: eventData.locationId,
                roomId: eventData.roomId,
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                message: translations.general.error,
            });
        } finally {
            isLoading.value = false;
        }
    }

    async function refreshOpenBillsList() {
        // if is already refreshing - enqueue next request and leave
        if (isRefreshingOpenBills.value == true) {
            isOpenBillsRefreshRequested.value = true;
            return;
        }

        // if is not refreshing - start refreshing
        // first reset previously set flag - if it was set to true
        isOpenBillsRefreshRequested.value = false;

        // start refreshing
        isRefreshingOpenBills.value = true;
        try {
            await openOrderForNowBillsListComponent.value?.refresh();
            await openOrderForNowOrLaterBillsListComponent.value?.refresh();
        } finally {
            isRefreshingOpenBills.value = false;
        }

        // if there is a new refresh request enqueued - refresh again - after 1 seconds
        if (isOpenBillsRefreshRequested.value) {
            setTimeout(refreshOpenBillsList, 1000);
        }
    }

    function onBillsLoadingError() {
        Popup.show({
            uniqueId: "open-bills-loading-error-popup",
            message: translations.openBills.failedLoadingBills,
        });
    }

    // register events
    onMounted(async () => {
        dataEvents.operatorSocket.BillOpened.add(refreshOpenBillsList);
        dataEvents.operatorSocket.BillChanged.add(refreshOpenBillsList);
        dataEvents.operatorSocket.BillDiscountChanged.add(refreshOpenBillsList);
        dataEvents.operatorSocket.BillNameChanged.add(refreshOpenBillsList);
        dataEvents.operatorSocket.BillClosed.add(refreshOpenBillsList);
        dataEvents.operatorSocket.TableCovidUserCountChanged.add(refreshOpenBillsList);
    });

    onBeforeUnmount(() => {
        dataEvents.operatorSocket.BillOpened.remove(refreshOpenBillsList);
        dataEvents.operatorSocket.BillChanged.remove(refreshOpenBillsList);
        dataEvents.operatorSocket.BillNameChanged.remove(refreshOpenBillsList);
        dataEvents.operatorSocket.BillDiscountChanged.remove(refreshOpenBillsList);
        dataEvents.operatorSocket.BillClosed.remove(refreshOpenBillsList);
        dataEvents.operatorSocket.TableCovidUserCountChanged.remove(refreshOpenBillsList);
    });

    async function onTabChange() {
        await nextTick();
        await openOrderForNowBillsListComponent.value?.redraw();
        await nextTick();
        await openOrderForNowOrLaterBillsListComponent.value?.redraw();
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .open-bills-view-container {
        position: absolute !important;
    }

    .open-bills-tab-view {
        .p-tabview-nav {
            padding-bottom: 1px;
        }
    }

    .open-bills-view {
        padding: 0.5em 2em 2em;

        .open-bills-tab > .loading-placeholder {
            text-align: center;
            padding: 1.5em 2em;
            line-height: 2em;
        }
    }
</style>
